import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, finalize, map, of, switchMap, timer } from 'rxjs';
import { LaneCustomer } from './lane.models';
import { LaneRoutingService } from './lane.service';

@Component({
  selector: 'app-lane-customer-picker', standalone: true, imports: [CommonModule, FormsModule],
  styles: [`:host { display: block; min-width: 0; }
    label { display: block; font-size: 12px; font-weight: 600; margin-bottom: 4px; }
    select,input { width: 100%; min-width: 0; border: 1px solid color-mix(in srgb, currentColor 20%, transparent); border-radius: 8px; background: var(--surface, white); color: inherit; padding: 8px 10px; }
    button { appearance: none; background: transparent; color: inherit; cursor: pointer; }
    .mode-button { border: 0; padding: 4px 0; color: #3b82f6; font-size: 13px; margin-top: 4px; }
    .result { display: block; width: 100%; text-align: left; padding: 8px 10px; border: 1px solid color-mix(in srgb, currentColor 20%, transparent); border-radius: 8px; margin-top: 4px; }
    .result:hover { background: #3b82f611; } .result strong,.result small { display: block; overflow-wrap: anywhere; }
    .hint { display: block; font-size: 12px; opacity: .7; margin-top: 7px; overflow-wrap: anywhere; }
    .error { color: #ef4444; font-size: 13px; margin-top: 4px; } button:disabled,select:disabled,input:disabled { opacity: .5; cursor: default; }`],
  template: `<label [for]="searchAll ? 'lane-customer-search' : 'lane-customer-select'">Customer</label>
    <ng-container *ngIf="!searchAll">
      <select title="Top 100 active customers by lading history" id="lane-customer-select" [ngModel]="value?.contact_uuid || ''" (ngModelChange)="choosePopular($event)" [disabled]="disabled || popularLoading">
        <option value="">{{ popularLoading ? 'Loading top customers…' : 'Choose a customer' }}</option>
        <option *ngIf="value && !inPopular(value)" [value]="value.contact_uuid">{{ optionLabel(value) }}</option>
        <option *ngFor="let customer of popular" [value]="customer.contact_uuid">{{ optionLabel(customer) }}</option>
      </select>
      
      <p class="error" role="alert" *ngIf="popularError">{{ popularError }} <button class="mode-button" [disabled]="disabled" (click)="loadPopular()">Retry</button></p>
      <button type="button" class="mode-button" [disabled]="disabled" (click)="setSearchMode(true)">Search all customers</button>
    </ng-container>
    <ng-container *ngIf="searchAll">
      <input id="lane-customer-search" [(ngModel)]="query" (ngModelChange)="search($event)" [disabled]="disabled" placeholder="Search customer name or account reference…" autocomplete="off">
      <small class="hint" *ngIf="value">Selected: {{ optionLabel(value) }}</small>
      <small class="hint" role="status" *ngIf="searchLoading">Finding customers…</small>
      <p class="error" role="alert" *ngIf="searchError">{{ searchError }}</p>
      <button type="button" class="result" *ngFor="let customer of results" [disabled]="disabled" (click)="choose(customer)">
        <strong>{{ customer.display_name }}</strong><small>{{ customer.account_reference || 'No account reference' }} · {{ customer.lading_count || 0 | number }} ladings{{ customer.active === false ? ' · Inactive' : '' }}</small>
      </button>
      <small class="hint" *ngIf="searched && !searchLoading && !searchError && !results.length">No matching customer contacts.</small>
      <button type="button" class="mode-button" [disabled]="disabled" (click)="setSearchMode(false)">Back to top 100 customers</button>
    </ng-container>
    <small class="hint" *ngIf="value?.active === false">Inactive customer; cannot approve a new route.</small>`
})
export class LaneCustomerPickerComponent implements OnInit {
  @Input() disabled = false;
  @Input() value: LaneCustomer | null = null;
  @Output() valueChange = new EventEmitter<LaneCustomer | null>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly searches = new Subject<string>();
  popular: LaneCustomer[] = []; popularLoading = false; popularError = '';
  searchAll = false; query = ''; results: LaneCustomer[] = []; searchLoading = false; searched = false; searchError = '';
  constructor(private readonly api: LaneRoutingService) {}
  ngOnInit(): void {
    this.searches.pipe(
      switchMap(query => query.trim().length < 2 ? of([]) : timer(250).pipe(
        switchMap(() => this.api.customers(query, 'search').pipe(map(result => result.data))),
        catchError(error => { this.searchError = error.error?.message || 'Customer search failed.'; return of([]); })
      )), takeUntilDestroyed(this.destroyRef)
    ).subscribe(customers => { this.results = customers; this.searchLoading = false; });
    this.loadPopular();
  }
  loadPopular(): void {
    if (this.popularLoading) return;
    this.popularLoading = true; this.popularError = '';
    this.api.popularCustomers().pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.popularLoading = false)).subscribe({
      next: result => this.popular = result.data,
      error: error => this.popularError = error.error?.message || 'Top customers could not be loaded.'
    });
  }
  inPopular(customer: LaneCustomer): boolean { return this.popular.some(row => row.contact_uuid === customer.contact_uuid); }
  optionLabel(customer: LaneCustomer): string {
    const account = customer.account_reference ? ' · ' + customer.account_reference : '';
    const usage = customer.lading_count === undefined ? '' : ' · ' + customer.lading_count.toLocaleString() + ' ladings';
    return customer.display_name + account + usage + (customer.active === false ? ' · Inactive' : '');
  }
  choosePopular(uuid: string): void {
    if (this.disabled) return;
    if (!uuid) { this.valueChange.emit(null); return; }
    const customer = this.popular.find(row => row.contact_uuid === uuid) || (this.value?.contact_uuid === uuid ? this.value : null);
    if (customer) this.valueChange.emit(customer);
  }
  choose(customer: LaneCustomer): void {
    if (this.disabled) return;
    this.valueChange.emit(customer); this.setSearchMode(false);
  }
  setSearchMode(enabled: boolean): void {
    this.searchAll = enabled; this.query = ''; this.results = []; this.searched = false; this.searchError = ''; this.searchLoading = false; this.searches.next('');
  }
  search(query: string): void {
    this.results = []; this.searchError = ''; this.searched = query.trim().length >= 2; this.searchLoading = this.searched; this.searches.next(query);
  }
}
